import { Core } from "@faire/design-tokens";
import { IBrandViewV2Response } from "@faire/web-api/indigofair/data/IBrandViewV2Response";
import brandPath from "@faire/web-api/routes/brand/brandToken";
import { HorizontalLine } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Link } from "@faire/web/ui/LinkAnchor";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React from "react";

import { Rating } from "@faire/retailer/app/visitor/[locale]/(pagesWithModal)/(pagesWithTopOfPageContent)/brand/[brandToken]/(rsc)/rsc/_sections/Info/Rating";
import { StoryModalButton } from "@faire/retailer/app/visitor/[locale]/(pagesWithModal)/(pagesWithTopOfPageContent)/brand/[brandToken]/(rsc)/rsc/_sections/Info/StoryModalButton";
import * as styles from "@faire/retailer/app/visitor/[locale]/(pagesWithModal)/(pagesWithTopOfPageContent)/brand/[brandToken]/(rsc)/rsc/_sections/Info/styles.css";
import { useFaireDirectFirstOrderIncentive } from "@faire/retailer/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import type { IReviewSummary } from "@faire/retailer/lib/buildReviewSummary";

import { SIGN_UP_BUTTON_WIDTH_DESKTOP } from "./consts";
import { SignUpButton } from "./SignUpButton";

interface IProps {
  brand: IBrandViewV2Response.ILeanBrand;
  reviewSummary: IReviewSummary | undefined;
  brandAboutSubheader: string | null;
}

export const FaireDirectBrandInfo: React.FC<IProps> = ({
  brand,
  reviewSummary,
  brandAboutSubheader,
}) => {
  return (
    <div className={styles.container}>
      <BrandNameSection brandName={brand.name} brandToken={brand.token} />
      <Spacer
        height={{
          mobileAndAbove: "3x",
          tabletAndAbove: "4x",
        }}
      />
      <SignUpButton brandToken={brand.token} />
      <Spacer
        height={{
          mobileAndAbove: "4x",
          tabletAndAbove: "5x",
        }}
      />
      <ResponsiveContentV2
        mobile={<HorizontalLine width="120px" />}
        tabletAndAbove={
          <HorizontalLine width={`${SIGN_UP_BUTTON_WIDTH_DESKTOP}px`} />
        }
      />
      <Spacer
        height={{
          mobileAndAbove: "4x",
          tabletAndAbove: "5x",
        }}
      />
      <ResponsiveContentV2
        tabletAndAbove={
          <>
            {reviewSummary ? <Rating reviewSummary={reviewSummary} /> : null}
            <Spacer height="2x" />

            {brand.description ? (
              <>
                <Typography color={Core.text.subdued} maxLines={3}>
                  {brand.description}
                </Typography>
                <Spacer height="2x" />
              </>
            ) : null}

            <StoryModalButton
              brand={brand}
              brandAboutSubheader={brandAboutSubheader}
            />
          </>
        }
      />
    </div>
  );
};

const BrandNameSection: React.FC<{
  brandName: string | undefined;
  brandToken: string | undefined;
}> = ({ brandName, brandToken }) => {
  const {
    isFaireDirectCreditPercentageBased,
    formattedPercentageBasedNumber,
    formattedMaxPercentageDiscountAmount,
    formattedFixedBasedAmount,
  } = useFaireDirectFirstOrderIncentive();

  if (!brandName) {
    return null;
  }

  return (
    <>
      <Link to={brandPath(brandToken ?? "")}>
        <Typography
          as="h1"
          variant={{
            mobileAndAbove: "displaySSerifRegular",
            tabletAndAbove: "displayMSerifRegular",
          }}
          align={{
            mobileAndAbove: "center",
            tabletAndAbove: "left",
          }}
          data-test-id="productGridHeader"
        >
          {brandName}
        </Typography>
      </Link>
      <Spacer
        height={{
          mobileAndAbove: "1x",
          tabletAndAbove: "2x",
        }}
      />
      <Typography
        as="h2"
        variant={{
          mobileAndAbove: "paragraphSansRegular",
          tabletAndAbove: "subheadingSansRegular",
        }}
        align={{
          mobileAndAbove: "center",
          tabletAndAbove: "left",
        }}
      >
        {isFaireDirectCreditPercentageBased ? (
          <StrictLocalMsg
            defaultMessage="We’ve partnered with Faire to make buying our products for your store simple. Sign up to get {faireDirectPercentageDiscountNumber} off your first order, up to {formattedMaxPercentageDiscountMoney}, and 1 year of free shipping."
            description={{
              text: "Subheader text for logged-out brand page",
              img: "https://cdn.faire.com/fastly/5456f752711f8519ed4c92215d7bfa1bcef6aca34863e36c82cf5c35805aac7a.png",
            }}
            values={{
              faireDirectPercentageDiscountNumber:
                formattedPercentageBasedNumber,
              formattedMaxPercentageDiscountMoney:
                formattedMaxPercentageDiscountAmount,
            }}
          />
        ) : (
          <StrictLocalMsg
            defaultMessage="We’ve partnered with Faire to make buying our products for your store simple. Sign up to get {discountMoney} off your first order, and 1 year of free shipping."
            description={{
              text: "Subheader text for logged-out brand page",
              img: "https://cdn.faire.com/fastly/5456f752711f8519ed4c92215d7bfa1bcef6aca34863e36c82cf5c35805aac7a.png",
            }}
            values={{
              discountMoney: formattedFixedBasedAmount,
            }}
          />
        )}
      </Typography>
    </>
  );
};
